import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerAccountsAdminApi, PartnerAccountAdminView } from '../../api/admin/partnerAccounts';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Check, X, Power, PlayCircle, Trash2 } from 'lucide-react';
import { projectsApi } from '../../api/projects';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected', suspended: 'Suspended' };
const PARTNER_TYPE_LABELS: Record<string, string> = { donor: 'Donor', implementing_partner: 'Implementing Partner', auditor: 'Auditor' };

/**
 * Real gap closed ("improve partner account management and access
 * and roles") - approving now requires picking the fixed type that
 * decides what the partner can actually do (view-only for Donor and
 * Auditor, view + submit for Implementing Partner). A dropdown row,
 * not a single click, since there's a real decision to make before
 * an account gets real access.
 */
function ApproveRow({ onApprove, isPending }: { onApprove: (partnerType: string) => void; isPending: boolean }) {
  const [partnerType, setPartnerType] = useState('');
  return (
    <span className="row-actions" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <select value={partnerType} onChange={(e) => setPartnerType(e.target.value)} style={{ fontSize: '0.85em' }}>
        <option value="">- choose type -</option>
        <option value="donor">Donor</option>
        <option value="implementing_partner">Implementing Partner</option>
        <option value="auditor">Auditor</option>
      </select>
      <IconActionButton icon={Check} label="Approve" disabled={!partnerType || isPending} onClick={() => onApprove(partnerType)} />
    </span>
  );
}

/** Real gap closed - approved partners previously had no way to be given real project visibility at all. A partner_project_links row is the only thing that grants it - never a blanket "sees everything" default. */
function ProjectLinksPanel({ approvedAccounts }: { approvedAccounts: PartnerAccountAdminView[] }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const linkedQuery = useQuery({
    queryKey: ['partner-linked-projects', selectedPartnerId],
    queryFn: () => partnerAccountsAdminApi.listLinkedProjects(Number(selectedPartnerId)),
    enabled: !!selectedPartnerId,
  });
  const projectsQuery = useQuery({ queryKey: ['all-projects-for-linking'], queryFn: () => projectsApi.list(), enabled: !!selectedPartnerId });

  const invalidateLinked = () => queryClient.invalidateQueries({ queryKey: ['partner-linked-projects', selectedPartnerId] });
  const linkMutation = useMutation({
    mutationFn: () => partnerAccountsAdminApi.linkProject(Number(selectedPartnerId), Number(selectedProjectId)),
    onSuccess: () => { showSuccess('Project linked.'); invalidateLinked(); setSelectedProjectId(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to link project.'),
  });
  const unlinkMutation = useMutation({
    mutationFn: (projectId: number) => partnerAccountsAdminApi.unlinkProject(Number(selectedPartnerId), projectId),
    onSuccess: () => { showSuccess('Project unlinked.'); invalidateLinked(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to unlink project.'),
  });

  return (
    <div className="panel">
      <SectionHeader title="Project Access" />
      <div className="panel-body">
        <p className="muted">Link an approved partner to the specific projects they should be able to see. No link means no visibility at all, regardless of type.</p>
        <label>
          Partner account
          <select value={selectedPartnerId} onChange={(e) => setSelectedPartnerId(e.target.value)}>
            <option value="">- choose an approved partner -</option>
            {approvedAccounts.map((a) => <option key={a.id} value={a.id}>{a.full_name} ({a.organization_name})</option>)}
          </select>
        </label>

        {selectedPartnerId && linkedQuery.data && (
          <>
            <ul className="deadline-list">
              {linkedQuery.data.data.length === 0 && <li className="muted">No projects linked yet.</li>}
              {linkedQuery.data.data.map((l) => (
                <li key={l.id}>
                  {l.project_name} ({l.project_reference})
                  {' '}<IconActionButton icon={Trash2} label="Unlink" danger disabled={unlinkMutation.isPending} onClick={() => unlinkMutation.mutate(l.project_id)} />
                </li>
              ))}
            </ul>
            <div className="inline-form">
              <label>
                Add project
                <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)}>
                  <option value="">- choose -</option>
                  {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name} ({p.project_reference})</option>)}
                </select>
              </label>
              <button disabled={!selectedProjectId || linkMutation.isPending} onClick={() => linkMutation.mutate()}>{linkMutation.isPending ? 'Linking…' : 'Link Project'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Real admin review for external partner-organization registrations.
 * Only accounts that have verified their email even appear in the
 * pending queue - an unverified/fake signup is never something an
 * admin has to sift through.
 */
export function PartnerAccountsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const allQuery = useQuery({ queryKey: ['partner-accounts-all'], queryFn: () => partnerAccountsAdminApi.listAll() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-accounts-all'] });

  const approveMutation = useMutation({ mutationFn: ({ id, partnerType }: { id: number; partnerType: string }) => partnerAccountsAdminApi.approve(id, partnerType), onSuccess: () => { showSuccess('Partner account approved.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const rejectMutation = useMutation({ mutationFn: ({ id, reason }: { id: number; reason: string }) => partnerAccountsAdminApi.reject(id, reason), onSuccess: () => { showSuccess('Partner account rejected.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const suspendMutation = useMutation({ mutationFn: (id: number) => partnerAccountsAdminApi.suspend(id), onSuccess: () => { showSuccess('Partner account suspended.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const reactivateMutation = useMutation({ mutationFn: (id: number) => partnerAccountsAdminApi.reactivate(id), onSuccess: () => { showSuccess('Partner account reactivated.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="page">
      <PageHeader
        title="Partner Accounts"
        description="External partner-organization registrations. Approving assigns a fixed type (Donor/Auditor: view-only; Implementing Partner: view + submit) - link accounts to specific projects below to grant real visibility."
      />

      <div className="panel">
        <SectionHeader title="Partner Account Registrations" />
        <div className="panel-body">
        {allQuery.data && (
          <DataTable<PartnerAccountAdminView>
            rows={allQuery.data.data}
            rowKey={(a) => a.id}
            emptyMessage="No partner account registrations yet."
            columns={[
              { header: 'Name', render: (a) => a.full_name },
              { header: 'Organization', render: (a) => a.organization_name },
              { header: 'Email', render: (a) => a.email },
              { header: 'Email Verified', render: (a) => a.email_verified_at ? 'Yes' : 'No' },
              { header: 'Type', render: (a) => a.partner_type ? PARTNER_TYPE_LABELS[a.partner_type] : '-' },
              { header: 'Status', render: (a) => <StatusBadge status={a.status} label={STATUS_LABELS[a.status]} /> },
              { header: 'Registered', render: (a) => a.created_at },
              {
                header: '',
                render: (a) => (
                  <span className="row-actions">
                    {a.status === 'pending_approval' && a.email_verified_at && (
                      <>
                        <ApproveRow isPending={approveMutation.isPending} onApprove={(partnerType) => approveMutation.mutate({ id: a.id, partnerType })} />
                        <IconActionButton icon={X} label="Reject" danger onClick={() => { const reason = window.prompt('Reason for rejection:'); if (reason) rejectMutation.mutate({ id: a.id, reason }); }} />
                      </>
                    )}
                    {a.status === 'approved' && <IconActionButton icon={Power} label="Suspend" onClick={() => suspendMutation.mutate(a.id)} />}
                    {a.status === 'suspended' && <IconActionButton icon={PlayCircle} label="Reactivate" onClick={() => reactivateMutation.mutate(a.id)} />}
                  </span>
                ),
              },
            ]}
          />
        )}
        </div>
      </div>

      <ProjectLinksPanel approvedAccounts={(allQuery.data?.data ?? []).filter((a) => a.status === 'approved')} />
    </div>
  );
}
